import { Link } from 'react-router-dom';
import { ArrowRight, Award, Clock, Heart, MapPin, User, Leaf, Sparkles } from 'lucide-react';
import { GiGardeningShears, GiFeather } from 'react-icons/gi';
import { MdCleaningServices } from 'react-icons/md';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ContactCTA } from '@/components/features/ContactCTA';
import { SITE_CONFIG } from '@/lib/constants';

const highlights = [
  {
    icon: <MapPin className="h-6 w-6" />,
    title: 'Tasmania Based',
    description: 'Proudly serving the Tasmanian community.',
  },
  {
    icon: <Clock className="h-6 w-6" />,
    title: 'Flexible Scheduling',
    description: 'Available to work around your schedule.',
  },
  {
    icon: <Award className="h-6 w-6" />,
    title: 'Quality Assured',
    description: 'Every job completed to the highest standard.',
  },
  {
    icon: <Heart className="h-6 w-6" />,
    title: 'Personal Service',
    description: 'Direct communication, no middlemen.',
  },
];

const services = [
  {
    icon: <GiGardeningShears className="h-6 w-6" />,
    title: 'Gardening',
    description: 'Transform your outdoor spaces',
  },
  {
    icon: <MdCleaningServices className="h-6 w-6" />,
    title: 'Cleaning',
    description: 'Spotless homes and businesses',
  },
  {
    icon: <GiFeather className="h-6 w-6" />,
    title: 'Taxidermy',
    description: 'Preserve nature\'s beauty',
  },
];

/**
 * About page component
 */
export function About() {
  return (
    <>
      {/* Hero Section with Photo */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom py-12 md:py-20">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <span className="category-label">About Me</span>
              <h1 className="text-4xl font-bold md:text-5xl">
                Meet the Person Behind {SITE_CONFIG.name}
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">
                A sole trader offering three distinct services with one common thread:
                quality craftsmanship and personal attention to every job.
              </p>

              {/* Quick Stats */}
              <div className="mt-8 flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Based in</p>
                    <p className="font-semibold">Tasmania</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Services</p>
                    <p className="font-semibold">3 Specialties</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button asChild className="gap-2">
                  <Link to="/contact">
                    Get in Touch
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Photo Section */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                {/* Main Photo Container */}
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted">
                  {/* Placeholder for owner photo - replace src with actual photo */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-muted text-muted-foreground">
                    <User className="h-24 w-24 mb-4 opacity-50" />
                    <p className="text-sm">Owner Photo</p>
                    <p className="text-xs mt-1 opacity-70">Add your photo here</p>
                  </div>
                  {/* Uncomment and update src when you have a photo:
                  <img
                    src="/images/owner-photo.jpg"
                    alt="Owner of Cloud9 Tasmania"
                    className="h-full w-full object-cover"
                  />
                  */}
                </div>

                {/* Decorative Elements */}
                <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-2xl bg-primary/20 -z-10" />
                <div className="absolute -top-4 -right-4 h-32 w-32 rounded-full bg-primary/10 -z-10" />

                {/* Floating Service Badge */}
                <div className="absolute -right-2 bottom-8 rounded-xl bg-card border border-border p-3 shadow-lg">
                  <div className="flex items-center gap-2">
                    <Leaf className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">Nature Enthusiast</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services I Offer */}
      <section className="py-12 md:py-16 bg-card">
        <div className="container-custom">
          <div className="grid gap-4 md:grid-cols-3">
            {services.map((service, index) => (
              <div
                key={index}
                className="flex items-center gap-4 rounded-xl border border-border bg-background p-4 transition-all hover:border-primary/50"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  {service.icon}
                </div>
                <div>
                  <h3 className="font-semibold">{service.title}</h3>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-20" aria-labelledby="my-story">
        <div className="container-custom">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="category-label">My Story</span>
              <h2 id="my-story" className="text-3xl font-bold">
                From Passion to Profession
              </h2>
              <div className="mt-6 space-y-4 text-muted-foreground">
                <p>
                  Welcome to Cloud9 Tasmania. As a sole trader based in beautiful Tasmania,
                  I offer a unique combination of services that reflect my diverse interests
                  and skills: gardening, cleaning, and taxidermy.
                </p>
                <p>
                  What might seem like an unusual mix actually shares a common foundation -
                  attention to detail, respect for the natural world, and a commitment to
                  quality in everything I do.
                </p>
                <p>
                  Whether I'm transforming an overgrown garden into a pristine outdoor space,
                  bringing a sparkling clean to your home, or preserving a treasured specimen
                  with the care it deserves, I bring the same dedication to every job.
                </p>
              </div>
            </div>

            {/* Philosophy Card */}
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="p-8">
                <span className="category-label">My Philosophy</span>
                <blockquote className="mt-2 text-2xl font-semibold leading-relaxed">
                  "Every task deserves full attention and care. Whether it's a lawn,
                  a living room, or a treasured specimen - the result should always
                  exceed expectations."
                </blockquote>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 md:py-16 bg-card" aria-labelledby="why-choose-me">
        <div className="container-custom">
          <div className="mb-10 text-center">
            <span className="category-label">Why Work With Me</span>
            <h2 id="why-choose-me" className="text-3xl font-bold">
              What Sets Me Apart
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map((item, index) => (
              <Card key={index} className="card-hover text-center">
                <CardContent className="pt-6">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
